import React, { useState, useCallback, useEffect, useRef } from "react";
import { useAtomValue } from "jotai";
import { AIBlockService } from "frontend/services/ai-blocks/ai-block-service";
import { toneChangeBlock } from "frontend/services/ai-blocks/edit-blocks/tone-change-block";
import { getElement } from "shared/datamodel/canvas-element";
import { undoRedoStackAtom } from "state-atoms/stage-atoms";
import { syncServiceAtom } from "state-atoms/general-atoms";
import panelStyle from "./ai-edits-panel.module.css";
import style from "./change-tone-button.module.css";
import { Floater } from "frontend/ui-components/floaters/floater";
import { Icon } from "frontend/canvas-designer-new/elements-toolbar-cim-restyle/toolbar-buttons";
import { useOnClickOutside } from "usehooks-ts";

interface ChangeToneButtonProps {
  selectedElementIds: string[];
  onClose?: () => void;
  onError?: (error: string) => void;
}

type ToneType = "casual" | "friendly" | "professional";

const TONE_OPTIONS: Array<{ label: string; value: ToneType }> = [
  { label: "Casual", value: "casual" },
  { label: "Friendly", value: "friendly" },
  { label: "Professional", value: "professional" },
];

export function ChangeToneButton({ selectedElementIds, onClose, onError }: ChangeToneButtonProps): React.ReactElement {
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSubmenu, setShowSubmenu] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef(null);
  const undoRedoStack = useAtomValue(undoRedoStackAtom);
  const syncService = useAtomValue(syncServiceAtom);
  useOnClickOutside(menuRef, () => setShowSubmenu(false));

  const aiBlockService = AIBlockService.getInstance();

  useEffect(() => {
    aiBlockService.registerBlock("tone-change", toneChangeBlock);
  }, []);

  const handleToneChange = useCallback(
    async (tone: ToneType) => {
      if (!syncService || selectedElementIds.length === 0) return;

      setIsProcessing(true);
      setShowSubmenu(false);

      try {
        const reflect = syncService.getReplicache();
        if (!reflect) {
          onError?.("Canvas not ready");
          return;
        }

        const elementsData = await reflect.query(async (tx) => {
          const elements = await Promise.all(
            selectedElementIds.map(async (id) => {
              const element = await getElement(tx, id);
              return { id, element };
            })
          );
          return elements;
        });

        const { edits } = await aiBlockService.generateEdits("tone-change", {
          prompt: `Change the tone of the following texts to ${tone}`,
          elementIds: elementsData.map((e) => e.id),
          texts: elementsData.map((e) => (e.element as { text?: string }).text || ""),
          tone,
        });

        const editsMap = new Map(edits.map((e) => [e.elementId, e]));
        const idsToUpdate = selectedElementIds.filter((id) => editsMap.has(id));

        let elementIndex = 0;
        await undoRedoStack?.patchElements?.(
          idsToUpdate,
          (element) => {
            const elementId = idsToUpdate[elementIndex];
            const edit = editsMap.get(elementId);
            if (edit) {
              Object.assign(element, edit.patch);
            }
            elementIndex += 1;
          },
          `tone-change-${tone}-${Date.now()}`
        );

        onClose?.();
      } catch (error_) {
        const errorMessage = error_ instanceof Error ? error_.message : "Failed to change tone";
        onError?.(errorMessage);
        console.error("Error changing tone:", error_);
      } finally {
        setIsProcessing(false);
      }
    },
    [syncService, selectedElementIds, aiBlockService, undoRedoStack, onClose, onError]
  );

  const isDisabled = selectedElementIds.length === 0 || isProcessing;

  return (
    <div className={style.container}>
      <button
        ref={buttonRef}
        className={panelStyle.actionButton}
        title="Change the tone or style of your text"
        onClick={() => setShowSubmenu(!showSubmenu)}
        disabled={isDisabled}
      >
        <Icon icon="change-tone" />
        {isProcessing ? "Processing..." : "Change Tone"}
        <svg
          width="4"
          height="7"
          viewBox="0 0 4 7"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          className={style.arrowIcon}
        >
          <path d="M0.5 0.500137L3.5 3.5" stroke="#83868F" strokeLinecap="round" />
          <path d="M0.5 6.50049L3.5 3.50063" stroke="#83868F" strokeLinecap="round" />
        </svg>
      </button>
      {showSubmenu && !isDisabled && (
        <Floater relativeTo={buttonRef} side="right" arrowSize={0} margin={12} ref={menuRef}>
          <div className={style.submenu}>
            {TONE_OPTIONS.map((option) => (
              <button
                key={option.value}
                className={style.submenuButton}
                onClick={() => handleToneChange(option.value)}
                disabled={isProcessing}
              >
                {option.label}
              </button>
            ))}
          </div>
        </Floater>
      )}
    </div>
  );
}
